function initiateGifts({ force = false } = {}) {
  if (window.cartGiftData && !force) return

  const scriptElement = document.getElementById("data-ajax-freebies")
  if (!scriptElement) return

  const jsonString = scriptElement.textContent
  if (!jsonString) return

  try {
    window.cartGiftData = JSON.parse(jsonString)
  } catch (error) {
    console.error("Failed to parse #data-ajax-freebies JSON:", error)
  }
}

function computeTotalItemsWithoutGift(cart) {
  if (!cart?.items?.length) return 0

  return cart.items.reduce((acc, item) => {
    if (item.price === 0) return acc

    const isPackageProtection = item.sku === "x-redo"
    if (isPackageProtection) return acc

    if (item.properties && item.properties["_add_more_save_more"]) return acc + item.quantity

    return acc
  }, 0)
}

function isSubscriptionCartLine(item) {
  if (!item) return false
  return Boolean(item.selling_plan_allocation) || item.selling_plan != null
}

/**
 * True when this line is a cart-drawer / script freebie instance.
 * Matches Function giftGateSubtotal exclusions (`_smgift` / `__isFreebie`).
 */
function isGiftThresholdCartLine(item) {
  if (!item) return false
  const props = item.properties
  if (!props || typeof props !== "object") return false
  const smgift = props._smgift
  const isFreebie = props.__isFreebie
  return (smgift != null && smgift !== "") || (isFreebie != null && isFreebie !== "")
}

function cartPropertyIsPresent(item, key) {
  const value = item?.properties?.[key]
  return value != null && value !== "" && value !== false
}

function normalizeCartId(id) {
  if (id == null) return null
  const match = String(id).match(/(\d+)(?!.*\d)/)
  return match ? match[1] : String(id)
}

function idSet(ids) {
  if (!Array.isArray(ids)) return new Set()
  return new Set(ids.map(normalizeCartId).filter(Boolean))
}

function readTransformGiftGateData() {
  const transform = window.cartGiftData?.transform
  if (!transform || typeof transform !== "object") return null
  const readPct = (value) => {
    const pct = Number(value)
    return Number.isFinite(pct) && pct > 0 && pct < 100 ? pct : 0
  }
  return {
    enabled: {
      lto: transform.enabled?.lto === true,
      ppup: transform.enabled?.ppup === true,
    },
    percent: {
      lto: readPct(transform.percent?.lto),
      ppup: readPct(transform.percent?.ppup),
    },
    canaryOnly: transform.canaryOnly !== false,
    eligible: {
      lto: idSet(transform.ltoEligibleProductIds),
      ppup: idSet(transform.ppupEligibleProductIds),
    },
    ineligible: idSet(transform.discountIneligibleProductIds),
  }
}

function transformOwnsGiftGateMode(transform, mode) {
  if (!transform || transform.enabled[mode] !== true) return false
  if (transform.canaryOnly !== false) return false
  const pct = transform.percent[mode]
  return Number.isFinite(pct) && pct > 0 && pct < 100
}

function transformClaimedGiftGateMode(item) {
  if (isGiftThresholdCartLine(item)) return null
  if (cartPropertyIsPresent(item, "_bundle_discount")) return null

  const lto = item?.properties?._is_limited_Time_cart_upsell === "true"
  const ppup = item?.properties?._is_pre_purchase_popup_upsell === "true"
  if (lto && ppup) return null
  if (lto) return "lto"
  if (ppup) return "ppup"
  return null
}

function transformGiftGateLineCents(item, transform) {
  const raw = Number(item?.original_line_price)
  if (!Number.isFinite(raw) || raw <= 0) return null
  if (isSubscriptionCartLine(item)) return raw

  const mode = transformClaimedGiftGateMode(item)
  if (!mode || !transformOwnsGiftGateMode(transform, mode)) return raw

  const productId = normalizeCartId(item?.product_id)
  if (!productId) return raw
  if (transform.ineligible.has(productId)) return raw
  if (!transform.eligible[mode].has(productId)) return raw

  const pct = transform.percent[mode]
  const reconstructed = Math.round((raw * 100) / (100 - pct))
  return Number.isFinite(reconstructed) && reconstructed > raw ? reconstructed : raw
}

/**
 * Dollar subtotal for freebie $ thresholds — sum of NON-gift lines'
 * list-basis price. Matches `giftGateSubtotal()` in line-property-discount so
 * BOGO/code/transform discounts do not yank a gift the Function would still
 * price as FREE GIFT.
 */
function computeGiftThresholdSubtotalDollars(cart) {
  if (!cart?.items?.length) return 0

  let cents = 0
  const transform = readTransformGiftGateData()
  for (const item of cart.items) {
    if (isGiftThresholdCartLine(item)) continue
    const transformCents = transformGiftGateLineCents(item, transform)
    if (Number.isFinite(transformCents) && transformCents > 0) {
      cents += transformCents
      continue
    }
    const original = Number(item.original_line_price)
    if (Number.isFinite(original) && original > 0) {
      cents += original
      continue
    }
    // Fallback when original_line_price is missing (rare): final line price.
    const final = Number(item.final_line_price)
    if (Number.isFinite(final) && final > 0) cents += final
  }
  return cents / 100
}

window.bbeComputeGiftThresholdSubtotalDollars = computeGiftThresholdSubtotalDollars

/**
 * Shopify cart/update.js cannot safely set qty by variant id when duplicate
 * variant lines exist. Use cart/change.js (line item key) for those; bulk
 * update only for variants that appear on exactly one cart line.
 */
function buildCartLineQuantityRequests(cart, qtyByVariantId) {
  const requests = []
  const bulkUpdates = Object.create(null)

  if (!cart?.items?.length) return requests

  for (const [rawVid, quantity] of Object.entries(qtyByVariantId)) {
    const variantId = Number(rawVid)
    if (!Number.isFinite(variantId)) continue

    const qty = Number(quantity)
    if (!Number.isFinite(qty)) continue

    const matchingItems = cart.items.filter((item) => Number(item.variant_id) === variantId)
    if (!matchingItems.length) continue

    const subscriptionLines = matchingItems.filter(isSubscriptionCartLine)
    const regularLines = matchingItems.filter((item) => !isSubscriptionCartLine(item))

    for (const item of subscriptionLines) {
      if (Number(item.quantity) === qty) continue
      const lineKey = item.key != null ? String(item.key).trim() : ""
      if (!lineKey) continue
      requests.push({
        type: "change",
        body: { id: lineKey, quantity: qty },
        info: { is_automatic: true },
      })
    }

    if (regularLines.length === 1 && matchingItems.length === 1) {
      bulkUpdates[String(variantId)] = qty
    } else if (regularLines.length > 0) {
      // Same variant on multiple lines (e.g. BOGO paid/free legs or one-time
      // + subscription) — cart/update.js by variant id is ambiguous; target
      // each intended line by key instead.
      for (const item of regularLines) {
        if (Number(item.quantity) === qty) continue
        const lineKey = item.key != null ? String(item.key).trim() : ""
        if (!lineKey) continue
        requests.push({
          type: "change",
          body: { id: lineKey, quantity: qty },
          info: { is_automatic: true },
        })
      }
    }
  }

  if (Object.keys(bulkUpdates).length > 0) {
    requests.push({
      type: "update",
      body: { updates: bulkUpdates },
      info: { is_automatic: true },
    })
  }

  return sanitizeCartMutationRequests(requests)
}

function sanitizeCartMutationRequests(requests) {
  if (!Array.isArray(requests) || !requests.length) return []

  return requests.filter((request) => {
    if (!request?.type || !request.body) return false

    if (request.type === "change") {
      const lineKey = request.body.id != null ? String(request.body.id).trim() : ""
      const quantity = Number(request.body.quantity)
      return Boolean(lineKey) && Number.isFinite(quantity)
    }

    if (request.type === "update") {
      const updates = request.body.updates
      return updates && typeof updates === "object" && Object.keys(updates).length > 0
    }

    return false
  })
}

function tryBeforeYouBuy() {
  const cart = window.liquidAjaxCart?.cart
  if (!cart?.items?.length || !cart.token) return null

  // Pair map: placeholder -> real product variant
  const PAIRS = [
    { placeholderId: 47489608908965, productId: 45998318813349 }, // White Tea 2 pack
    { placeholderId: 47489608941733, productId: 45998318878885 }, // Citrus 2 pack
    { placeholderId: 47489608974501, productId: 45998318846117 }, // Sweet Amber 2 pack
    { placeholderId: 47541781725349, productId: 45960968339621 }, // White Tea 1 pack
    { placeholderId: 47541781758117, productId: 45960963522725 }, // Citrus 1 pack
    { placeholderId: 47541781790885, productId: 45960968372389 }, // Sweet Amber 1 pack
  ]

  try {
    const qtyByVariantId = new Map()

    for (const item of cart.items) {
      const vid = Number(item.variant_id)
      qtyByVariantId.set(vid, Number(item.quantity || 0))
    }

    const updates = Object.create(null)

    for (const { placeholderId, productId } of PAIRS) {
      const placeholderQty = qtyByVariantId.get(placeholderId)
      const productQty = qtyByVariantId.get(productId)

      const hasPlaceholder = typeof placeholderQty === "number"
      const hasProduct = typeof productQty === "number"

      // Rule: placeholder cannot exist without product
      if (hasPlaceholder && !hasProduct) {
        if (placeholderQty !== 0) {
          updates[placeholderId] = 0
        }
        continue
      }

      // Rule: if both exist, placeholder must match product qty (product wins)
      if (hasPlaceholder && hasProduct && placeholderQty !== productQty) {
        updates[placeholderId] = productQty
      }
    }

    const requests = buildCartLineQuantityRequests(cart, updates)
    if (!requests.length) return null

    return { requests }
  } catch (error) {
    console.error("Error in tryBeforeYouBuy:", error)
  }
}

/**
 * True when the SERVER is still granting this gift for free.
 *
 * The Function (`line-property-discount`, `_smgift` branch) is the pricing authority
 * for gifts: a still-qualifying gift line comes back at $0. If it is $0, the server
 * has decided the shopper has earned it, and the client must not overrule that.
 *
 * WHY THIS GUARD EXISTS. `CartGiftMutation` removes a gift whose threshold now exceeds
 * the client-computed gate subtotal. That gate sums PRE-discount `original_line_price`.
 * Under the upsell-price-transform (cart.transform.run), an upsell line's price is
 * OVERRIDDEN rather than discounted, so `original_line_price` collapses to the reduced
 * figure and the gate drops — on the observed cart, $80.26 -> $67.94. Without this
 * guard the client would then strip a gift the shopper had already legitimately
 * unlocked, and the server was still giving free.
 *
 * It is also strictly safer than the previous behaviour in the BOGO case the comment
 * below describes: whenever the two views disagree, the server wins.
 *
 * Removal still proceeds once the server actually starts CHARGING for the line
 * (final > 0), which is the case where removing genuinely protects the shopper from an
 * unexpected charge.
 */
function serverStillGrantsGiftLine(item) {
  const final = Number(item?.final_line_price)
  if (Number.isFinite(final) && final <= 0) return true

  // Defensive: a full-line allocation is the same statement in a different shape.
  const original = Number(item?.original_line_price)
  if (!Number.isFinite(original) || original <= 0) return false
  let allocated = 0
  for (const alloc of item?.line_level_discount_allocations || []) {
    const amount = Number(alloc?.amount)
    if (Number.isFinite(amount) && amount > 0) allocated += amount
  }
  return allocated >= original
}

function CartGiftMutation() {
  const gifts = window.cartGiftData
  const cart = window.liquidAjaxCart?.cart

  // Merchant "Show cart gifts" off → zero gift work (no threshold scans / removes).
  if (gifts && gifts.enabled === false) return
  if (!gifts?.freebies?.length || !cart?.items?.length || !cart.token) return

  try {
    window.totalItemsWithoutGift = computeTotalItemsWithoutGift(cart)

    // Use pre-discount non-gift subtotal (NOT cart.total_price). Post-BOGO
    // total_price can sit under $80 while paid merch still clears the Function
    // FREE GIFT gate — stripping here orphaned gifts and broke BOGO carts.
    const gateSubtotal = computeGiftThresholdSubtotalDollars(cart)
    const requests = []

    for (const freebie of gifts.freebies) {
      const freebieVariantId = Number(freebie.product)
      if (!Number.isFinite(freebieVariantId)) continue
      if (freebie.threshold == null || !(freebie.threshold > gateSubtotal)) continue

      // Remove by line key so a paid same-SKU line is never zeroed.
      for (const item of cart.items) {
        if (Number(item.variant_id) !== freebieVariantId) continue
        if (!isGiftThresholdCartLine(item)) continue
        // Server is the pricing authority — never strip a gift it still grants free.
        if (serverStillGrantsGiftLine(item)) continue
        const lineKey = item.key != null ? String(item.key).trim() : ""
        if (!lineKey) continue
        requests.push({
          type: "change",
          body: { id: lineKey, quantity: 0 },
          info: { is_automatic: true, bbeGiftThresholdRemove: true },
        })
      }
    }

    const safeRequests = sanitizeCartMutationRequests(requests)
    if (!safeRequests.length) return

    return { requests: safeRequests }
  } catch (error) {
    console.error("Error in CartGiftMutation:", error)
  }
}

document.addEventListener("liquid-ajax-cart:request-end", (event) => {
  const { requestState, cart } = event.detail
  initiateGifts({ force: true })

  if (cart?.items) {
    window.totalItemsWithoutGift = computeTotalItemsWithoutGift(cart)
  }

  // add-to-cart succeeded
  if (requestState.requestType !== "add" || !requestState.responseData?.ok) return

  // avoid opening drawer over upsell
  if (document.querySelector(".pre-upsell__wrapper.show")) return

  if (requestState.info?.should_keep_drawer_closed) return

  const drawer = document.querySelector("cart-drawer")
  if (!drawer) return

  // Upsell adds while the drawer is already open — skip re-open/focus churn.
  if (drawer.hasAttribute("open")) return

  // Bug 3 (2026-05-21): defensive guard against the "drawer opens then
  // immediately closes" report from collection/page ATC. Only auto-open
  // when the cart `item_count` actually grew. If the cart didn't change
  // (e.g. a duplicate request-end fired from a follow-up mutation like
  // CartGiftMutation / tryBeforeYouBuy, or the add was a server-side no-op),
  // we skip the open call — preventing the second open() from racing the
  // first one's slide-in animation and unmounting / re-mounting the
  // drawer mid-animation. We compare against the prior count cached on
  // window so the check works across multiple successive ATC clicks.
  const prevCount = Number(window.__bbePrevCartItemCount)
  const newCount = Number(cart?.item_count)
  if (Number.isFinite(prevCount) && Number.isFinite(newCount) && newCount <= prevCount) {
    if (Number.isFinite(newCount)) window.__bbePrevCartItemCount = newCount
    return
  }
  if (Number.isFinite(newCount)) window.__bbePrevCartItemCount = newCount

  // Honor an explicit user-close that happened *during* this add's flight: if
  // the shopper closed the drawer after clicking add but before the request
  // resolved, respect that intent and do not reopen. A fresh add clears the
  // flag at request-start (see below), so this only blocks a close that raced
  // the in-flight request — it does not block a normal ATC. (F-07a)
  if (drawer.dataset.userClosed === "true") return

  drawer.open()
})

// On a fresh user-initiated add, clear any stale user-closed flag so the drawer
// can open at request-end. We clear at request-START (not end) on purpose: a
// close that happens *during* the flight re-sets the flag and wins the guard
// above. Adds explicitly flagged to keep the drawer closed are left untouched.
document.addEventListener("liquid-ajax-cart:request-start", (event) => {
  const { requestState } = event.detail || {}
  if (requestState?.requestType !== "add") return
  if (requestState?.info?.should_keep_drawer_closed) return

  const drawer = document.querySelector("cart-drawer")
  if (drawer && drawer.dataset.userClosed === "true") {
    delete drawer.dataset.userClosed
  }
})

// Seed the cached count so the very first ATC isn't gated by "no prior count".
if (typeof window.__bbePrevCartItemCount === "undefined") {
  document.addEventListener("liquid-ajax-cart:init", (event) => {
    const initialCount = Number(event?.detail?.cart?.item_count)
    if (Number.isFinite(initialCount)) {
      window.__bbePrevCartItemCount = initialCount
    }
  })
  document.addEventListener(
    "DOMContentLoaded",
    () => {
      if (typeof window.__bbePrevCartItemCount !== "undefined") return
      const fallback = Number(window.liquidAjaxCart?.cart?.item_count)
      if (Number.isFinite(fallback)) {
        window.__bbePrevCartItemCount = fallback
      }
    },
    { once: true },
  )
}

initiateGifts()

liquidAjaxCart.conf("updateOnWindowFocus", false)
// Allow qty 0 so minus at 1 removes the line (default keeps minus disabled at 1).
liquidAjaxCart.conf("quantityTagAllowZero", true)
// Library default is 300ms before a qty-stepper click even STARTS its request;
// 150ms still coalesces double-clicks but shaves perceived latency.
liquidAjaxCart.conf("quantityTagDebounce", 150)
liquidAjaxCart.conf("mutations", [CartGiftMutation, tryBeforeYouBuy])
